import React from "react";
import styled from "@emotion/styled";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useTheme, alpha } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";

const ChartWrapper = styled.div`
  height: 160px;
  width: 100%;
`;

const barTopLabels = {
  id: "barTopLabels",
  afterDraw(chart) {
    const { ctx, data } = chart;
    ctx.save();
    const meta = chart.getDatasetMeta(0);
    const isDark = chart.options.scales.y.ticks.color === "rgba(255, 255, 255, 0.7)";
    meta.data.forEach((bar, index) => {
      const value = data.datasets[0].data[index];
      if (value !== undefined && value !== null) {
        const x = bar.x;
        const y = bar.y;

        // Draw Count (Value)
        ctx.fillStyle = isDark ? "rgba(255, 255, 255, 0.95)" : "#1e293b";
        ctx.font = "bold 12px 'Inter', sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(value, x, y - 5);
      }
    });
    ctx.restore();
  },
};

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const ProBarChart = ({
  total = 0,
  active = 0,
  online = 0,
  offline = 0,
  expired = 0,
  suspended = 0,
  onHold = 0,
  onBarClick,
  activeLabel,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [hoveredLabelIndex, setHoveredLabelIndex] = React.useState(null);

  const chartData = React.useMemo(() => {
    return {
      labels: ["Total", "Active", "Online", "Offline", "Expired", "Suspended", "On Hold"],
      datasets: [
        {
          label: "Count",
          backgroundColor: [
            "#a855f7", // Total - Purple
            "#3b82f6", // Active - Blue
            "#22c55e", // Online - Green
            isDarkMode ? "#e2e8f0" : "#cbd5e1", // Offline
            "#f97316", // Expired - Orange
            "#ef4444", // Suspended - Red
            "#eab308", // On Hold - Yellow
          ],
          hoverBackgroundColor: [
            "#9333ea",
            "#2563eb",
            "#16a34a",
            "#94a3b8",
            "#ea580c",
            "#dc2626",
            "#ca8a04",
          ],
          borderRadius: 6,
          data: [total, active, online, offline, expired, suspended, onHold],
          barPercentage: 0.6,
          categoryPercentage: 0.8,
        },
      ],
    };
  }, [total, active, online, offline, expired, suspended, onHold, isDarkMode]);

  const chartRef = React.useRef(null);

  const handleChartClick = (event) => {
    const chart = chartRef.current;
    if (!chart) return;

    // Get the canvas element
    const canvas = chart.canvas;
    if (!canvas) return;

    // Calculate relative position manually for better compatibility
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const xAxis = chart.scales.x;

    // Get the index based on the x-coordinate
    const index = xAxis.getValueForPixel(x);

    if (index >= 0 && index < chartData.labels.length) {
      const label = chartData.labels[index];
      if (onBarClick) onBarClick(label);
    }
  };

  const options = {
    maintainAspectRatio: false,
    animation: {
      duration: 750,
      easing: "easeInOutQuart",
    },
    onHover: (event, elements, chart) => {
      const { x, y } = event;
      const xAxis = chart.scales.x;
      const index = xAxis.getValueForPixel(x);
      const isWithinXRange = index >= 0 && index < chartData.labels.length;

      // Ensure cursor is pointer when over bars or labels
      const isOverLabel = y > chart.chartArea.bottom;
      const isInteractive = elements.length > 0 || (isOverLabel && isWithinXRange);

      if (chart.canvas) {
        chart.canvas.style.cursor = isInteractive ? "pointer" : "default";
      }

      // Update hovered label for visual highlighting
      const newHoveredIndex = isWithinXRange && isOverLabel ? index : null;
      if (newHoveredIndex !== hoveredLabelIndex) {
        setHoveredLabelIndex(newHoveredIndex);
        chart.update("none");
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: isDarkMode ? "#1e293b" : "#1e293b",
        titleFont: { size: 14, weight: "bold" },
        bodyFont: { size: 13 },
        padding: 12,
        borderRadius: 8,
        displayColors: true,
        callbacks: {
          title: (context) => context[0].label,
          label: (context) => `Count: ${context.raw}`,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          display: true,
          color: (context) => {
            const index = context.index;
            const colors = [
              "#a855f7", // Total
              "#3b82f6", // Active
              "#22c55e", // Online
              isDarkMode ? "#e2e8f0" : "#94a3b8", // Offline
              "#f97316", // Expired
              "#ef4444", // Suspended
              "#eab308", // On Hold
            ];
            return colors[index] || "#2563eb";
          },
          font: (context) => {
            const index = context.index;
            const label = chartData.labels[index];
            const isHighlighted = index === hoveredLabelIndex || label === activeLabel;
            return {
              size: 11,
              weight: isHighlighted ? 700 : 600,
              family: "'Inter', sans-serif",
            };
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(0,0,0,0.04)",
          drawBorder: false,
        },
        ticks: {
          display: true,
          color: isDarkMode ? "rgba(255, 255, 255, 0.7)" : "#94a3b8",
          font: {
            size: 10,
          },
          precision: 0,
        },
        grace: "25%",
      },
    },
  };

  return (
    <Box onClick={handleChartClick} sx={{ cursor: "pointer" }}>
      <ChartWrapper>
        <Bar ref={chartRef} data={chartData} options={options} plugins={[barTopLabels]} />
      </ChartWrapper>
    </Box>
  );
};

export default ProBarChart;
